import { EnumSirenProfile, EnumAiAutomationLevel, DetectionLabel } from './siteApi';
import { EnumContactPreference, } from '../Alert/types';
import { z } from 'zod';
import { EnumAlertStatus } from '@/lib/api/types.gen';

export const siteFormValidation = {
  name: {
    required: 'Site name is required',
  },
  timeZone: {
    required: 'Timezone is required',
  },
  address: {
    lat: {
      required: 'Latitude is required',
    },
    lon: {
      required: 'Longitude is required',
    },
  },
  newMonitoringAlertsAfterSeconds: {
    min: {
      value: 0,
      message: 'Value must be greater than or equal to 0',
    },
  },
  sirenCooldownSeconds: {
    min: {
      value: 0,
      message: 'Value must be greater than or equal to 0',
    },
  },
};

export interface ContactFormValues {
  name: string;
  lastName?: string;
  email?: string;
  phone?: string;
  role?: string;
  contactPreference?: EnumContactPreference;
  companyName?: string;
  order?: number;
}

const normalizeOptionalString = (value: unknown) =>
  typeof value === 'string' ? value.trim() : value;

const optionalEmailSchema = z.preprocess(
  (value) => {
    const normalized = normalizeOptionalString(value);
    return normalized === '' ? undefined : normalized;
  },
  z.string().email('Invalid email address').optional()
);

const optionalPhoneSchema = z.preprocess(
  (value) => {
    const normalized = normalizeOptionalString(value);
    return normalized === '' ? undefined : normalized;
  },
  z
    .string()
    .regex(/^\+[1-9]\d{1,14}$/, 'Phone number must start with + followed by country code')
    .optional()
);

export const contactFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  lastName: z.string().optional(),
  email: optionalEmailSchema,
  phone: optionalPhoneSchema,
  role: z.string().optional(),
  contactPreference: z.nativeEnum(EnumContactPreference).optional(),
  companyName: z.string().optional(),
  order: z.number().optional(),
}).superRefine((data, ctx) => {
  if (!data.email && !data.phone) {
    const message = 'Email or phone is required';
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message,
      path: ['email'],
    });
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message,
      path: ['phone'],
    });
  }

  const phoneRequiredPreferences = [
    EnumContactPreference.SmsThenCall,
    EnumContactPreference.SmsOnly,
    EnumContactPreference.CallOnly,
  ];

  if (data.contactPreference && phoneRequiredPreferences.includes(data.contactPreference) && !data.phone) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${data.contactPreference.replace(/([A-Z])/g, ' $1').trim()} requires a phone number`,
      path: ['contactPreference'],
    });
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Phone number is required for this preference',
      path: ['phone'],
    });
  }
});

export const dispositionWorkflowConfig = z.record(
  z.nativeEnum(EnumAlertStatus),
  z.array(
    z.object({
      name: z.string(),
      steps: z.array(
        z.object({
          stepNumber: z.number(),
          hasNote: z.boolean().default(false),
          hasCheckbox: z.boolean().default(false),
          title: z.string(),
          instructions: z.string().optional(),
        })
      ),
      isDefault: z.boolean(),
      schedule: z
        .object({
          days: z.array(
            z.object({
              day: z.string(), // Day of week as string ("Sunday", "Monday", etc.)
              timeRanges: z.array(
                z.object({
                  start: z.string(), // TimeSpan as string in format "HH:MM:SS"
                  end: z.string(), // TimeSpan as string in format "HH:MM:SS"
                })
              ),
            })
          ),
        })
        .optional()
        .nullable(),
    })
  )
);

// Form schema
export const siteFormSchema = z.object({
  name: z.string().min(1, 'Site name is required'),
  timeZone: z.string().min(1, 'Timezone is required'),
  address: z.object({
    addressLine1: z.string().optional(),
    addressLine2: z.string().optional(),
    city: z.string().optional(),
    postCode: z.string().optional(),
    region: z.string().optional(),
    country: z.string().optional(),
    lat: z.number().optional(),
    lon: z.number().optional(),
  }),
  newMonitoringAlertsAfterSeconds: z.number().min(0).default(0),
  siteNotes: z.string().optional(),
  sirenProfile: z.nativeEnum(EnumSirenProfile),
  sirenCooldownSeconds: z.number().min(0),
  sirenProfilePerson: z.nativeEnum(EnumSirenProfile),
  sirenProfileVehicle: z.nativeEnum(EnumSirenProfile),
  sirensUnlinked: z.boolean(),
  aiAutomationLevel: z.nativeEnum(EnumAiAutomationLevel),
  aiPrimer: z.string().optional(),
  noThreatAiPrimer: z.string().optional(),
  detectionLabels: z.array(z.nativeEnum(DetectionLabel)).optional(),
  objectDetectionSensitivity: z.number().min(30).max(95).optional(),
  guardResponseEnabled: z.boolean(),
  guardResponseDispatchCode: z.string().optional(),
  complianceEnabled: z.boolean(),
  customerId: z.string().optional(),
  externalServiceOptions: z.record(z.string(), z.record(z.string(), z.string())).optional(),
  dispositionWorkflowConfig: dispositionWorkflowConfig.optional(),
  defaultRouteNamespaceId: z.string().nullable().optional(),
});

export type SiteFormValues = z.infer<typeof siteFormSchema>;
